import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../../shared/api/api-error";
import { setCsrfToken } from "../../shared/api/csrf";
import { authApi } from "./auth-api";
import type { AdminOperator, LoginInput } from "./auth-contracts";
import { AuthContext, type AuthContextValue, type AuthStatus } from "./auth-context";

const sessionIdleMilliseconds = 30 * 60 * 1_000;
const sessionChannelName = "gb-admin-session";

type SessionMessage =
  | { type: "activity"; at: number }
  | { type: "ended" };

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [operator, setOperator] = useState<AdminOperator | null>(null);
  const operatorId = useRef<string | null>(null);
  const sessionGeneration = useRef(0);
  const idleDeadline = useRef<number | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const channel = useRef<BroadcastChannel | null>(null);

  const clearIdleTimer = useCallback(() => {
    if (idleTimer.current !== null) clearTimeout(idleTimer.current);
    idleTimer.current = null;
    idleDeadline.current = null;
  }, []);

  const clearLocalSession = useCallback(() => {
    sessionGeneration.current += 1;
    clearIdleTimer();
    setCsrfToken(null);
    operatorId.current = null;
    setOperator(null);
    setStatus("unauthenticated");
    queryClient.clear();
  }, [clearIdleTimer, queryClient]);

  const endSessionEverywhere = useCallback(() => {
    clearLocalSession();
    channel.current?.postMessage({ type: "ended" } satisfies SessionMessage);
  }, [clearLocalSession]);

  const armIdleLock = useCallback(
    (activityAt = Date.now()) => {
      if (idleTimer.current !== null) clearTimeout(idleTimer.current);
      const deadline = activityAt + sessionIdleMilliseconds;
      idleDeadline.current = deadline;
      idleTimer.current = setTimeout(
        () => endSessionEverywhere(),
        Math.max(0, deadline - Date.now())
      );
    },
    [endSessionEverywhere]
  );

  const applySession = useCallback(
    (session: Awaited<ReturnType<typeof authApi.session>>) => {
      setCsrfToken(session.csrfToken ?? null);
      if (session.authenticated && session.operator) {
        if (operatorId.current !== session.operator.id) queryClient.clear();
        operatorId.current = session.operator.id;
        setOperator(session.operator);
        setStatus("authenticated");
        const at = Date.now();
        armIdleLock(at);
        channel.current?.postMessage({ type: "activity", at } satisfies SessionMessage);
        return;
      }
      clearLocalSession();
    },
    [armIdleLock, clearLocalSession, queryClient]
  );

  const refreshSession = useCallback(async () => {
    const generation = sessionGeneration.current;
    const session = await authApi.session();
    if (sessionGeneration.current !== generation) return;
    applySession(session);
  }, [applySession]);

  useEffect(() => {
    const controller = new AbortController();
    const generation = sessionGeneration.current;
    void authApi
      .session(controller.signal)
      .then((session) => {
        if (sessionGeneration.current === generation) applySession(session);
      })
      .catch(() => {
        if (!controller.signal.aborted && sessionGeneration.current === generation) {
          clearLocalSession();
        }
      });
    return () => controller.abort();
  }, [applySession, clearLocalSession]);

  useEffect(() => {
    if (typeof BroadcastChannel === "function") {
      const broadcastChannel = new BroadcastChannel(sessionChannelName);
      channel.current = broadcastChannel;
      broadcastChannel.onmessage = (event: MessageEvent<SessionMessage>) => {
        if (event.data.type === "ended") clearLocalSession();
        if (event.data.type === "activity" && status === "authenticated") {
          armIdleLock(event.data.at);
        }
      };
    }

    function sessionExpired() {
      endSessionEverywhere();
    }

    function sessionActive() {
      if (status !== "authenticated") return;
      const at = Date.now();
      armIdleLock(at);
      channel.current?.postMessage({ type: "activity", at } satisfies SessionMessage);
    }

    window.addEventListener("gb-admin-session-expired", sessionExpired);
    window.addEventListener("gb-admin-session-active", sessionActive);
    return () => {
      window.removeEventListener("gb-admin-session-expired", sessionExpired);
      window.removeEventListener("gb-admin-session-active", sessionActive);
      channel.current?.close();
      channel.current = null;
    };
  }, [armIdleLock, clearLocalSession, endSessionEverywhere, status]);

  useEffect(() => {
    if (status !== "authenticated") return;
    let checking = false;

    async function verifyVisibleSession() {
      if (document.visibilityState !== "visible" || checking) return;
      if (idleDeadline.current !== null && Date.now() >= idleDeadline.current) {
        endSessionEverywhere();
        return;
      }
      checking = true;
      const generation = sessionGeneration.current;
      try {
        await refreshSession();
      } catch {
        if (sessionGeneration.current === generation) endSessionEverywhere();
      } finally {
        checking = false;
      }
    }

    function onPageShow() {
      void verifyVisibleSession();
    }

    document.addEventListener("visibilitychange", onPageShow);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onPageShow);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [endSessionEverywhere, refreshSession, status]);

  useEffect(() => clearIdleTimer, [clearIdleTimer]);

  const login = useCallback(
    async (input: LoginInput) => {
      sessionGeneration.current += 1;
      const generation = sessionGeneration.current;
      const session = await authApi.login(input);
      if (sessionGeneration.current !== generation) return;
      if (!session.authenticated || !session.operator) {
        clearLocalSession();
        throw new ApiError({
          status: 403,
          code: "ADMIN_SESSION_REQUIRED",
          message: "Esta conta não possui uma sessão administrativa válida."
        });
      }
      applySession(session);
    },
    [applySession, clearLocalSession]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 401)) throw error;
    }
    endSessionEverywhere();
  }, [endSessionEverywhere]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      operator,
      isAuthenticated: status === "authenticated",
      login,
      logout,
      refreshSession
    }),
    [status, operator, login, logout, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
